/**
 * 认证上下文
 * 管理用户登录状态和权限
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { App } from 'antd';
import { User, login as apiLogin, logout as apiLogout, getCurrentUser } from '../services/authService';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    isAuthenticated: boolean;
    login: (username: string, password: string) => Promise<boolean>;
    logout: () => Promise<void>;
    checkAuth: () => Promise<void>;
    hasRole: (roles: string | string[]) => boolean;
    isAdmin: boolean;
    isAnalyst: boolean;
    isViewer: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { message } = App.useApp();
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    // 检查认证状态
    const checkAuth = useCallback(async () => {
        try {
            const response = await getCurrentUser();
            if (response.authenticated && response.user) {
                setUser(response.user);
            } else {
                setUser(null);
            }
        } catch (error) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // 初始化时检查认证状态
    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // 登录
    const login = async (username: string, password: string): Promise<boolean> => {
        try {
            const response = await apiLogin(username, password);
            if (response.success && response.user) {
                setUser(response.user);
                message.success(`欢迎回来，${response.user.username}！`);
                return true;
            }
            return false;
        } catch (error: any) {
            const errorMsg = error.response?.data?.error || '登录失败';
            message.error(errorMsg);
            return false;
        }
    };

    // 登出
    const logout = async () => {
        try {
            await apiLogout();
            setUser(null);
            message.success('已退出登录');
        } catch (error) {
            // 即使 API 失败也清除本地状态
            setUser(null);
        }
    };

    // 检查角色权限
    const hasRole = (roles: string | string[]): boolean => {
        if (!user) return false;
        const roleList = Array.isArray(roles) ? roles : [roles];
        return roleList.includes(user.role) || user.is_superuser;
    };

    const value: AuthContextType = {
        user,
        loading,
        isAuthenticated: !!user,
        login,
        logout,
        checkAuth,
        hasRole,
        isAdmin: user?.role === 'admin' || user?.is_superuser || false,
        isAnalyst: hasRole(['admin', 'analyst']),
        isViewer: hasRole(['admin', 'analyst', 'viewer']),
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export default AuthContext;
